import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";

import AuthContext from "../../store/auth-context";
import gs from "../../services/global";
import axios from "axios";

import Header from "../../components/Header";
import InnerBgComp from "../../components/InnerBgComp";
import { FlowSteps, stepFun, toasterConfig } from "../../utils/Constant";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import animationData from "../../assests/json/loader_1.json";
import { Player } from "@lottiefiles/react-lottie-player";

const {
    REACT_APP_DEVELOPMENTURL,
    REACT_APP_FLOWID,
} = process.env;

// Proves the returning device started the ID check. Minted once and never rotated until the step
// completes: Veriff bakes the return URL into the session at creation, and a reused session — which
// KYC hands back per user, not per journey — still carries the token it was created with.
const IDV_HANDOFF_KEY = "idvHandoff";

const IdentityVerification = () => {
    const authCtx = useContext(AuthContext);
    const instanceId = authCtx?.instanceId;

    const navigate = useNavigate();

    const [searchParams] = useSearchParams();
    const isCallback = searchParams.get('callback') === '1';
    const [disableBtn, setDisableBtn] = useState(false);
    const [termCheckbox, setTermCheckbox] = useState(false);
    const [resumeUrl, setResumeUrl] = useState(null);
    // Veriff returned to a device that did not start this journey — usually the QR hand-off phone.
    const [isForeignDevice] = useState(() => {
        if (!isCallback) {
            return false;
        }

        const handoffToken = searchParams.get('h');
        const startedHere = !!handoffToken
            && localStorage.getItem(IDV_HANDOFF_KEY) === handoffToken;

        return !startedHere || !localStorage.getItem("instancesId");
    });
    const [polling, setPolling] = useState(isCallback && !isForeignDevice);
    const [initializing, setInitializing] = useState(!isCallback);

    const loaderAnim = useRef(null);
    const animPlay = () => {
        loaderAnim.current.play();
    };

    // Resume an existing verification rather than letting a refresh create a duplicate session.
    useEffect(() => {
        if (isCallback || !instanceId) {
            setInitializing(false);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const result = await axios
                    .post(`/flow/${REACT_APP_FLOWID}/instances/${instanceId}`, { action: "fetch", data: {} });

                if (cancelled) return;

                const currentStep = result?.data?.currentStepId;
                if (currentStep && currentStep !== FlowSteps.IDENTITY_VERIFICATION) {
                    navigate(stepFun()[currentStep], { replace: true });
                    return;
                }

                const responseData = result?.data?.responseData;
                if (responseData?.isIdentityVerificationCompleted) {
                    setPolling(true);
                } else if (responseData?.hasIdentityVerificationSession && responseData?.url) {
                    setResumeUrl(responseData.url);
                }
            } catch (error) {
                // Fall through to the consent screen.
            } finally {
                if (!cancelled) setInitializing(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isCallback, instanceId]);

    useEffect(() => {
        if (!polling) return;
        const interval = setInterval(async () => {
            try {
                let payload = {
                    action: "check",
                    data: {},
                };

                const result = await axios
                    .post(`/flow/${REACT_APP_FLOWID}/instances/${instanceId}`, payload);

                const currentStep = result?.data?.currentStepId;
                const responseData = result?.data?.responseData;
                const resubmitUrl = responseData?.url;
                const errorMessages = result?.data?.errors;

                if (currentStep && currentStep !== FlowSteps.IDENTITY_VERIFICATION) {
                    if (responseData?.preDeclinedStepId) {
                        localStorage.setItem("preDeclinedStepId", responseData.preDeclinedStepId);
                    }
                    localStorage.removeItem(IDV_HANDOFF_KEY);
                    navigate(stepFun()[currentStep], { replace: true });
                    return;
                }

                // Expired or abandoned at Veriff: back to the consent screen for a fresh session.
                if (responseData?.retryIdentityVerification) {
                    setPolling(false);
                    return;
                }

                if (resubmitUrl) {
                    window.location.href = resubmitUrl;
                    return;
                }

                if (errorMessages && errorMessages.length > 0) {
                    toast.error("Something went wrong.", toasterConfig);
                }
            } catch (error) {
                toast.error("Something went wrong.", toasterConfig);
            }
        }, 3000);

        return () => clearInterval(interval);
    }, [polling]);

    const {
        handleSubmit,
    } = useForm({
        mode: "all",
    });

    const onSubmit = () => {
        if (resumeUrl) {
            setDisableBtn(true);
            gs.showLoader(true);
            window.location.href = resumeUrl;
            return;
        }

        // Veriff only accepts HTTPS return URLs; on local http the backend falls back to its own.
        const data = {};
        if (window.location.protocol === "https:") {
            const handoffToken = localStorage.getItem(IDV_HANDOFF_KEY)
                ?? window.crypto?.randomUUID?.()
                ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            localStorage.setItem(IDV_HANDOFF_KEY, handoffToken);

            const returnParams = new URLSearchParams({ callback: "1", h: handoffToken });
            data.redirectUrl = `${window.location.origin}/identity-verification?${returnParams.toString()}`;
        }

        let payload = {
            action: "submit",
            data,
        };

        setDisableBtn(true);
        gs.showLoader(true);

        axios
            .post(`/flow/${REACT_APP_FLOWID}/instances/${instanceId}`, payload)
            .then((response) => {
                const result = response?.data;
                const redirectUrl = result?.responseData?.url;

                if (redirectUrl) {
                    window.location.href = redirectUrl;
                } else {
                    toast.error("Something went wrong.", toasterConfig);
                }

                setDisableBtn(false);
                gs.showLoader(false);
            })
            .catch((error) => {
                const errorEnd = error?.response?.data?.currentStepId;
                navigate(stepFun()[errorEnd], { replace: true });
                const errorResult = error?.response?.data?.errors;
                const arrayOfString = Object.values(errorResult);
                const arrayFlatten = arrayOfString.flat();

                setDisableBtn(false);
                gs.showLoader(false);

                for (let itemData of arrayFlatten) {
                    toast.error(itemData, toasterConfig);
                }
            });
    };

    localStorage.setItem("stUrl", REACT_APP_DEVELOPMENTURL);
    localStorage.setItem("flowId", REACT_APP_FLOWID);

    const showConsent = !isForeignDevice && !polling && !initializing;
    const showVerifying = !isForeignDevice && (polling || initializing);

    return (
        <div>
            <Header />
            <div className="main-container">
                <InnerBgComp classes={"inner-bg-img"} />

                <div className="container">
                    {isForeignDevice && (
                        <motion.div
                            initial={{ y: 60, scale: 0.9, opacity: 0 }}
                            animate={{ y: 0, scale: 1, opacity: 1 }}
                            transition={{ duration: 0.5 }}
                            className="comm-box congo-checkout"
                        >
                            <div className="comm-hdn-slide">
                                <div className="comm-hdn-box">
                                    <h3 className="comm-hdn">That&rsquo;s your ID check done</h3>
                                    <div className="comm-para">
                                        <p>
                                            We have received your identity verification. To finish your
                                            application, go back to the device you started it on &mdash; it will
                                            carry on from there.
                                        </p>
                                        <p>You can close this page.</p>
                                    </div>
                                </div>
                                <div className="tick-anim">
                                    <i className="icon-check" />
                                </div>
                            </div>
                        </motion.div>
                    )}
                    {showConsent && (
                        <motion.div
                            initial={{ y: 60, scale: 0.9, opacity: 0 }}
                            animate={{ y: 0, scale: 1, opacity: 1 }}
                            transition={{ duration: 0.5 }}
                            className="comm-box congo-checkout"
                        >
                            <div className="comm-hdn-slide">
                                <div className="comm-hdn-box">
                                    <h3 className="comm-hdn">We need to verify your identity</h3>
                                    <div className="comm-para">
                                        <p>You will be redirected to Veriff to complete a quick identity verification. Please have a valid photo ID ready.</p>
                                    </div>
                                </div>
                                <div className="tick-anim">
                                    <i className="icon-check" />
                                </div>
                            </div>
                            <form id="identityForm" onSubmit={handleSubmit(onSubmit)}>
                                <div className="check-box">
                                    <input
                                        type="checkbox"
                                        name="termCheckbox"
                                        id="term"
                                        defaultChecked={termCheckbox}
                                        onClick={(e) => setTermCheckbox(e.target.checked)}
                                    />
                                    <label className="check-label" htmlFor="term">
                                        I agree to be redirected to a third-party partner for the verification of my identity.
                                    </label>
                                </div>
                                <button className="button button--block" disabled={!termCheckbox || disableBtn}>Continue</button>
                            </form>
                        </motion.div>
                    )}
                    {showVerifying && (
                        <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.5 }}
                            className="comm-box congo-box"
                        >
                            <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.5 }}
                                className="congo-lottie"
                            >
                                <Player
                                    onEvent={(event) => {
                                        if (event === "load") {
                                            animPlay();
                                        }
                                    }}
                                    ref={loaderAnim}
                                    autoplay={true}
                                    loop={true}
                                    controls={true}
                                    src={animationData}
                                />
                            </motion.div>
                            <div className="congo-cont">
                                <div>
                                    <motion.h4
                                        initial={{ y: 60, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ duration: 0.5 }}
                                        className="congo-hdn"
                                    >
                                        Almost done
                                    </motion.h4>
                                    <motion.div
                                        initial={{ y: 60, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ duration: 0.5, delay: 0.2 }}
                                        className="congo-para"
                                    >
                                        <p>
                                            We are verifying your identity.
                                        </p>
                                    </motion.div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default IdentityVerification;
